/**
 * Produktthementeaser Slider Component
 * Handles responsive slider with dynamic navigation
 */
export class ProduktthementeaserSlider {
	constructor(containerId, options = {}) {
		this.containerId = containerId
		this.container = document.querySelector(`.produktthementeaser-container-${containerId}`)

		if (!this.container) {
			console.warn(`Slider container not found for ID: ${containerId}`)
			return
		}

		// Default options
		this.options = {
			totalItems: options.totalItems || 0,
			breakpoints: {
				mobile: { max: 639, slidesPerView: 1 },
				tablet: { min: 640, max: 1023, slidesPerView: 2 },
				desktop: { min: 1024, slidesPerView: 4 },
			},
			...options,
		}

		// Initialize properties
		this.slider = this.container.querySelector(`.produktthementeaser-slider-${containerId} .flex`)
		this.currentSlide = 0
		this.slidesPerView = 1
		this.maxSlides = 0

		// Initialize if we have items
		if (this.options.totalItems > 0) {
			this.init()
		}
	}

	init() {
		this.calculateSlidesPerView()
		this.setupEventListeners()
		this.updateSlider()
		this.handleResize()
	}

	calculateSlidesPerView() {
		const width = window.innerWidth
		const { breakpoints } = this.options

		if (width >= breakpoints.desktop.min) {
			this.slidesPerView = breakpoints.desktop.slidesPerView
		} else if (width >= breakpoints.tablet.min) {
			this.slidesPerView = breakpoints.tablet.slidesPerView
		} else {
			this.slidesPerView = breakpoints.mobile.slidesPerView
		}

		this.maxSlides = Math.ceil(this.options.totalItems / this.slidesPerView)
		this.updateNavigationVisibility()
		this.updateDots()
	}

	updateNavigationVisibility() {
		const needsNav = this.options.totalItems > this.slidesPerView

		// Desktop navigation
		const prevBtn = this.container.querySelector(`.produktthementeaser-prev-${this.containerId}`)
		const nextBtn = this.container.querySelector(`.produktthementeaser-next-${this.containerId}`)

		// Tablet navigation
		const prevTablet = this.container.querySelector(`.produktthementeaser-prev-tablet-${this.containerId}`)
		const nextTablet = this.container.querySelector(`.produktthementeaser-next-tablet-${this.containerId}`)

		// Mobile navigation
		const prevMobile = this.container.querySelector(`.produktthementeaser-prev-mobile-${this.containerId}`)
		const nextMobile = this.container.querySelector(`.produktthementeaser-next-mobile-${this.containerId}`)

		// Show/hide based on viewport and need
		const width = window.innerWidth

		if (width >= 1024) {
			// Desktop
			this.toggleVisibility(prevBtn, this.options.totalItems > 4)
			this.toggleVisibility(nextBtn, this.options.totalItems > 4)
		} else if (width >= 640) {
			// Tablet
			this.toggleVisibility(prevTablet, this.options.totalItems > 2)
			this.toggleVisibility(nextTablet, this.options.totalItems > 2)
		} else {
			// Mobile
			this.toggleVisibility(prevMobile, this.options.totalItems > 1)
			this.toggleVisibility(nextMobile, this.options.totalItems > 1)
		}
	}

	toggleVisibility(element, show) {
		if (element) {
			element.style.display = show ? '' : 'none'
		}
	}

	updateSlider() {
		if (this.slider) {
			const translateX = this.currentSlide * 100
			this.slider.style.transform = `translateX(-${translateX}%)`
		}
		this.updateDotStates()
	}

	updateDots() {
		const dotsContainer = this.container.querySelector(`.produktthementeaser-dots-${this.containerId}`)
		if (!dotsContainer) return

		// Clear existing dots
		dotsContainer.innerHTML = ''

		// Only show dots if there's more than one slide
		if (this.maxSlides > 1) {
			for (let i = 0; i < this.maxSlides; i++) {
				const dot = document.createElement('button')
				dot.className =
					'w-2 h-2 rounded-full transition-colors ' + (i === 0 ? 'bg-red-600' : 'bg-gray-300 hover:bg-gray-400')
				dot.setAttribute('aria-label', `Slide ${i + 1}`)
				dot.addEventListener('click', () => this.slideTo(i))
				dotsContainer.appendChild(dot)
			}
		}
	}

	updateDotStates() {
		const dots = this.container.querySelectorAll(`.produktthementeaser-dots-${this.containerId} button`)
		dots.forEach((dot, index) => {
			if (index === this.currentSlide) {
				dot.classList.add('bg-red-600')
				dot.classList.remove('bg-gray-300')
			} else {
				dot.classList.remove('bg-red-600')
				dot.classList.add('bg-gray-300')
			}
		})
	}

	slideNext() {
		if (this.maxSlides > 1) {
			this.currentSlide = (this.currentSlide + 1) % this.maxSlides
			this.updateSlider()
		}
	}

	slidePrev() {
		if (this.maxSlides > 1) {
			this.currentSlide = this.currentSlide === 0 ? this.maxSlides - 1 : this.currentSlide - 1
			this.updateSlider()
		}
	}

	slideTo(index) {
		if (index >= 0 && index < this.maxSlides) {
			this.currentSlide = index
			this.updateSlider()
		}
	}

	setupEventListeners() {
		// All navigation buttons
		const navigationButtons = [
			{ selector: `.produktthementeaser-prev-${this.containerId}`, handler: () => this.slidePrev() },
			{ selector: `.produktthementeaser-next-${this.containerId}`, handler: () => this.slideNext() },
			{ selector: `.produktthementeaser-prev-tablet-${this.containerId}`, handler: () => this.slidePrev() },
			{ selector: `.produktthementeaser-next-tablet-${this.containerId}`, handler: () => this.slideNext() },
			{ selector: `.produktthementeaser-prev-mobile-${this.containerId}`, handler: () => this.slidePrev() },
			{ selector: `.produktthementeaser-next-mobile-${this.containerId}`, handler: () => this.slideNext() },
		]

		navigationButtons.forEach(({ selector, handler }) => {
			const button = this.container.querySelector(selector)
			if (button) {
				button.addEventListener('click', handler)
			}
		})

		// Touch support
		this.addTouchSupport()
	}

	addTouchSupport() {
		let touchStartX = 0
		let touchEndX = 0

		this.slider?.addEventListener('touchstart', e => {
			touchStartX = e.changedTouches[0].screenX
		})

		this.slider?.addEventListener('touchend', e => {
			touchEndX = e.changedTouches[0].screenX
			this.handleSwipe(touchStartX, touchEndX)
		})
	}

	handleSwipe(startX, endX) {
		const threshold = 50 // Minimum swipe distance
		const diff = startX - endX

		if (Math.abs(diff) > threshold) {
			if (diff > 0) {
				// Swipe left - next slide
				this.slideNext()
			} else {
				// Swipe right - previous slide
				this.slidePrev()
			}
		}
	}

	handleResize() {
		let resizeTimer
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer)
			resizeTimer = setTimeout(() => {
				this.calculateSlidesPerView()
				// Reset to first slide on resize to prevent out of bounds
				if (this.currentSlide >= this.maxSlides) {
					this.currentSlide = 0
				}
				this.updateSlider()
			}, 250)
		})
	}

	// Public method to refresh slider
	refresh() {
		this.calculateSlidesPerView()
		this.updateSlider()
	}

	// Destroy slider instance
	destroy() {
		// Remove event listeners if needed
		this.container = null
		this.slider = null
	}
}

// Auto-initialize sliders on DOM ready
if (typeof window !== 'undefined') {
	window.ProduktthementeaserSlider = ProduktthementeaserSlider
}
